import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import {
  listSdTestDevices,
  SDTESTAPP_DEFAULT_PORT,
  SdTestAppHttpClient,
  type SdTestConnectionStatus,
  type SdTestDemographics,
} from "@/lib/sdtestapp/sdTestAppHttpClient";
import { SdTestAppBenchClient } from "@/lib/sdtestapp/sdTestAppBenchClient";
import { resolveSdTestAppControlLevel } from "@/lib/sdtestapp/sdTestAppControlLevel";
import { SdTestAppRelayClient } from "@/lib/sdtestapp/sdTestAppRelayClient";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";

import { ConfigurationSdTestConnectionPanel } from "./ConfigurationSdTestConnectionPanel";
import { fetchBenchMachine, fetchBenchMachines } from "./benchesApi";
import { benchDisplayLabel } from "./benchesLabel";
import { BenchBatchTestsTab } from "./bench/BenchBatchTestsTab";
import { BenchCommandDocsTab } from "./bench/BenchCommandDocsTab";
import { BenchDocumentationTab } from "./bench/BenchDocumentationTab";
import { BenchFirmwareTab } from "./bench/BenchFirmwareTab";
import { BenchMainTab } from "./bench/BenchMainTab";
import { BenchSettingsTab } from "./bench/BenchSettingsTab";
import { BENCH_DETAIL_TABS, type BenchDetailTabId } from "./bench/benchTabTypes";

const PAGE_HELP =
  "Full SDTestApp bench workstation UI for all NiceGUI tabs except Production. Production runs on Cal+Test. " +
  "Local loopback actuates devices; remote users can observe device demographics when relay is online.";

function activeDevices(demographics: SdTestDemographics | null) {
  return listSdTestDevices(demographics);
}

export function BenchDetailPage(): JSX.Element {
  const { machineId: machineIdParam = "" } = useParams();
  const machineId = String(machineIdParam).trim();
  const writesAllowed = useHubModuleWrite("manufacturing");

  const [port, setPort] = useState(SDTESTAPP_DEFAULT_PORT);
  const [controlMessage, setControlMessage] = useState<string | null>(null);
  const [isLocalBench, setIsLocalBench] = useState(false);
  const [connection, setConnection] = useState<SdTestConnectionStatus>("connecting");
  const [localDemographics, setLocalDemographics] = useState<SdTestDemographics | null>(null);
  const [observeStatusText, setObserveStatusText] = useState("Not connected");
  const [observeDemographics, setObserveDemographics] = useState<SdTestDemographics | null>(null);
  const [activeTab, setActiveTab] = useState<BenchDetailTabId>("main");
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const [referenceConnectorId, setReferenceConnectorId] = useState<string | null>(null);

  const benchQuery = useQuery({
    queryKey: ["bench-machine", machineId],
    queryFn: () => fetchBenchMachine(machineId),
    enabled: machineId.length > 0,
    refetchInterval: 30_000,
  });

  const benchesQuery = useQuery({
    queryKey: ["bench-machines"],
    queryFn: fetchBenchMachines,
    enabled: benchQuery.isSuccess && benchQuery.data == null,
  });

  const bench = useMemo(() => {
    if (benchQuery.data) return benchQuery.data;
    return (benchesQuery.data ?? []).find((row) => row.machineId === machineId) ?? null;
  }, [benchQuery.data, benchesQuery.data, machineId]);

  const demographics = isLocalBench ? localDemographics : observeDemographics;
  const devices = activeDevices(demographics);
  const canActuate = isLocalBench && writesAllowed;
  const deviceCount = devices.length;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!machineId) return;
      const resolution = await resolveSdTestAppControlLevel(machineId, port);
      if (!cancelled) {
        setIsLocalBench(resolution.controlLevel === "local");
        setControlMessage(resolution.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machineId, port]);

  useEffect(() => {
    if (!machineId || !isLocalBench) {
      setConnection("disconnected");
      setLocalDemographics(null);
      return undefined;
    }

    const client = new SdTestAppHttpClient(port, {
      onDemographics: setLocalDemographics,
      onStatusChange: (_status, connectionStatus) => setConnection(connectionStatus),
    });
    client.startPolling(3000);
    return () => client.dispose();
  }, [machineId, isLocalBench, port]);

  useEffect(() => {
    if (!machineId || isLocalBench || !bench?.online) {
      setObserveStatusText(bench?.online === false ? "Bench offline" : "Not connected");
      setObserveDemographics(null);
      return undefined;
    }

    const relayClient = new SdTestAppRelayClient(machineId, {
      onStatusChange: (status) => setObserveStatusText(status.text),
    });

    void relayClient.refresh().then((nextDemographics) => {
      setObserveDemographics(nextDemographics);
    });
    relayClient.startPolling(5000);

    return () => {
      relayClient.dispose();
    };
  }, [machineId, isLocalBench, bench?.online]);

  useEffect(() => {
    if (!selectedConnectorId && devices[0]) {
      setSelectedConnectorId(String(devices[0].connectorId ?? ""));
    }
  }, [devices, selectedConnectorId]);

  useEffect(() => {
    if (!canActuate) return undefined;
    const benchClient = new SdTestAppBenchClient(port);
    const intervalId = setInterval(() => {
      void benchClient
        .fetchSlots()
        .then((slots) => setReferenceConnectorId(slots.referenceConnectorId))
        .catch(() => undefined);
    }, 4000);
    return () => clearInterval(intervalId);
  }, [canActuate, port]);

  if (!machineId) {
    return (
      <div className="mx-auto max-w-6xl p-4">
        <p className="text-sm text-muted-foreground">Bench id is missing.</p>
        <Link to="/manufacturing/benches" className="text-sm text-primary underline">
          Back to benches
        </Link>
      </div>
    );
  }

  const title = bench ? benchDisplayLabel(bench) : machineId;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/manufacturing/benches"
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Benches
        </Link>
        <Link to="/manufacturing/cal-test" className="text-sm text-primary underline">
          Open Cal+Test
        </Link>
      </div>

      <PageTitleWithHelp hoverHint={PAGE_HELP}>{title}</PageTitleWithHelp>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">SDTestApp</h2>
          {bench ? (
            <Badge tone={bench.online ? "success" : "muted"}>{bench.online ? "Online" : "Offline"}</Badge>
          ) : benchQuery.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          {bench ? (
            <p className="text-xs text-muted-foreground">
              Engine {bench.engineVersion ?? "unknown"} · last seen {new Date(bench.lastSeenAt).toLocaleString()}
            </p>
          ) : null}

          {isLocalBench ? (
            <>
              <p className="text-sm text-green-800">SDTestApp on this PC matches this bench — Hub uses loopback.</p>
              <ConfigurationSdTestConnectionPanel
                port={port}
                onPortChange={setPort}
                connection={connection}
                activeCount={deviceCount}
                onReconnect={() => void new SdTestAppHttpClient(port).refresh()}
              />
            </>
          ) : (
            <>
              {controlMessage ? <p className="text-sm text-amber-800">{controlMessage}</p> : null}
              <p className="text-sm">
                Relay observe: <span className="font-medium">{observeStatusText}</span>
                {deviceCount > 0 ? ` · ${deviceCount} active device${deviceCount === 1 ? "" : "s"}` : null}
              </p>
              <p className="text-sm text-muted-foreground">
                Remote observe: settings, firmware catalog, command registry, documentation, and device console logs are available. Actuation requires SDTestApp on the bench PC.
              </p>
            </>
          )}
        </CardBody>
      </Card>

      <div className="flex flex-wrap gap-2">
        {BENCH_DETAIL_TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            size="sm"
            variant={activeTab === tab.id ? "primary" : "secondary"}
            className={cn(activeTab === tab.id && "shadow-sm")}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardBody>
          {activeTab === "main" ? (
            <BenchMainTab
              port={port}
              machineId={machineId}
              devices={devices}
              canActuate={canActuate}
              observeOnly={!isLocalBench}
              selectedConnectorId={selectedConnectorId}
              onSelectConnector={setSelectedConnectorId}
              referenceConnectorId={referenceConnectorId}
              onReferenceChange={() => {
                void new SdTestAppBenchClient(port)
                  .fetchSlots()
                  .then((slots) => setReferenceConnectorId(slots.referenceConnectorId))
                  .catch(() => undefined);
              }}
            />
          ) : activeTab === "batch-tests" ? (
            <BenchBatchTestsTab
              port={port}
              devices={devices}
              canActuate={canActuate}
              selectedConnectorId={selectedConnectorId}
              onSelectConnector={setSelectedConnectorId}
            />
          ) : activeTab === "firmware-testing" ? (
            <BenchFirmwareTab
              port={port}
              machineId={machineId}
              connectorId={selectedConnectorId}
              canActuate={canActuate}
              observeOnly={!isLocalBench}
            />
          ) : activeTab === "documentation" ? (
            <BenchDocumentationTab port={port} machineId={machineId} observeOnly={!isLocalBench} />
          ) : activeTab === "settings" ? (
            <BenchSettingsTab port={port} machineId={machineId} canActuate={canActuate} observeOnly={!isLocalBench} />
          ) : (
            <BenchCommandDocsTab
              port={port}
              machineId={machineId}
              connectorId={selectedConnectorId}
              canActuate={canActuate}
              observeOnly={!isLocalBench}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
